import { api } from '@/lib/api';
import { isIos, isStandalone } from '@/lib/device';
import { m } from '@/lib/i18n';

export { isIos, isStandalone };
export const pushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/** Where notifications stand on *this* device (the server only knows about devices, not permissions). */
export type PushStatus = 'on' | 'off' | 'blocked' | 'unsupported' | 'install-first';

export async function pushStatus(): Promise<PushStatus> {
  if (!pushSupported()) return isIos() && !isStandalone() ? 'install-first' : 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  if (Notification.permission !== 'granted') return 'off';
  const reg = await navigator.serviceWorker.ready;
  return (await reg.pushManager.getSubscription()) ? 'on' : 'off';
}

function urlB64ToUint8Array(base64: string) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

const sameKey = (a: ArrayBuffer | null, b: Uint8Array) => !!a && a.byteLength === b.byteLength && new Uint8Array(a).every((v, i) => v === b[i]);

/**
 * A push subscription for the server's current key. A subscription made for an
 * older key (e.g. the server's keys changed on a redeploy) can never deliver,
 * so it's replaced.
 */
async function subscription(reg: ServiceWorkerRegistration) {
  const { publicKey } = await api<{ publicKey: string }>('GET', '/push/key');
  const key = urlB64ToUint8Array(publicKey);
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub.options.applicationServerKey, key)) {
    await sub.unsubscribe().catch(() => {});
    sub = null;
  }
  return sub ?? reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
}

/** Ask for permission and register this device for reminders. Throws a readable error. */
export async function enablePush() {
  if (!pushSupported()) throw new Error(isIos() && !isStandalone() ? m.push_ios_first() : m.push_unsupported());
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error(m.push_blocked());
  const reg = await navigator.serviceWorker.ready;
  const sub = await subscription(reg);
  await api('POST', '/push/subscribe', { subscription: sub.toJSON() });
}

/**
 * On start: if this device allows notifications, make sure the server has a
 * working subscription for it. Returns true when something was (re)registered.
 */
export async function syncPush() {
  if (!pushSupported() || Notification.permission !== 'granted') return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await subscription(reg);
    await api('POST', '/push/subscribe', { subscription: sub.toJSON() });
    return true;
  } catch {
    return false; // not critical: Settings shows the real state and a button
  }
}
